import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer, ColumnLayer, PolygonLayer } from '@deck.gl/layers';
import Map from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { apiFetch } from '../constants';

const INITIAL_VIEW = { longitude: 14.5, latitude: 47.5, zoom: 7, pitch: 45, bearing: 0 };
const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

const LV_PALETTE = [
  [255, 99, 132], [54, 162, 235], [255, 206, 86], [75, 192, 192],
  [153, 102, 255], [255, 159, 64], [0, 204, 150], [255, 72, 72],
  [72, 220, 255], [220, 72, 255],
];

const MV_PALETTE = [
  [56, 189, 248], [168, 85, 247], [34, 197, 94], [249, 115, 22],
];

function substationRect(lon, lat, widthM, depthM) {
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos(lat * Math.PI / 180);
  const dLon = (widthM / 2) / mPerDegLon;
  const dLat = (depthM / 2) / mPerDegLat;
  return [[
    [lon - dLon, lat - dLat],
    [lon + dLon, lat - dLat],
    [lon + dLon, lat + dLat],
    [lon - dLon, lat + dLat],
    [lon - dLon, lat - dLat],
  ]];
}

function useTileData(backendUrl, tileUrls, name) {
  const [data, setData] = useState(null);
  const url = tileUrls?.[name];
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    apiFetch(`${backendUrl}${url}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => console.error(`[MapView] Failed to load ${name}:`, err));
    return () => { cancelled = true; };
  }, [backendUrl, url]);
  return data;
}

export default function MapView({ tileUrls, backendUrl, layerVisibility, highlightedGrid, onGridClick }) {
  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [tooltip, setTooltip] = useState(null);
  const hasZoomed = useRef(false);

  const lvData      = useTileData(backendUrl, tileUrls, 'lv_grid');
  const mvData      = useTileData(backendUrl, tileUrls, 'mv_grid');
  const buildingsData = useTileData(backendUrl, tileUrls, 'buildings');

  // Auto-zoom to bounding box of first dataset that loads
  useEffect(() => {
    if (hasZoomed.current) return;
    const source = lvData || buildingsData || mvData;
    if (!source || source.features.length === 0) return;

    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const f of source.features) {
      const geom = f.geometry;
      if (!geom) continue;
      const coords = geom.type === 'Point'      ? [geom.coordinates]          :
                     geom.type === 'LineString'  ? geom.coordinates            :
                     geom.type === 'Polygon'     ? geom.coordinates[0]         : [];
      for (const [lon, lat] of coords) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
    if (minLon === Infinity) return;

    const lon   = (minLon + maxLon) / 2;
    const lat   = (minLat + maxLat) / 2;
    const delta = Math.max(maxLon - minLon, maxLat - minLat);
    const zoom  = delta < 0.02 ? 14 : delta < 0.06 ? 13 : delta < 0.15 ? 12 : 11;

    hasZoomed.current = true;
    setViewState(v => ({ ...v, longitude: lon, latitude: lat, zoom }));
  }, [lvData, buildingsData, mvData]);

  const handleHover = useCallback(({ object, x, y }) => {
    setTooltip(object ? { object, x, y } : null);
  }, []);

  const handleViewStateChange = useCallback(({ viewState: vs }) => setViewState(vs), []);

  const vis    = useMemo(() => layerVisibility || {}, [layerVisibility]);
  const showLV = vis.lv_grid !== false;
  const showMV = vis.mv_grid !== false;

  const focusedKeys = useMemo(() => {
    const s = new Set();
    if (highlightedGrid) s.add(highlightedGrid);
    return s;
  }, [highlightedGrid]);
  const hasFocus = focusedKeys.size > 0;
  const focusTrigger = useMemo(() => Array.from(focusedKeys).sort().join(','), [focusedKeys]);

  // Assign a stable color per LV grid (trafo service area) — must be defined before lvCableColor
  const lvGridColorMap = useMemo(() => {
    if (!lvData) return {};
    const ids = [...new Set(lvData.features.map(f => f.properties?.lv_grid).filter(Boolean))].sort();
    return Object.fromEntries(ids.map((id, i) => [id, LV_PALETTE[i % LV_PALETTE.length]]));
  }, [lvData]);

  // Assign a stable color per MV grid — must be defined before mvCableColor
  const mvGridColorMap = useMemo(() => {
    if (!mvData) return {};
    const ids = [...new Set(mvData.features.map(f => f.properties?.mv_trafo).filter(Boolean))].sort();
    return Object.fromEntries(ids.map((id, i) => [id, MV_PALETTE[i % MV_PALETTE.length]]));
  }, [mvData]);

  // Returns full [r,g,b,a] for LV cable/node features — white when focused, dim when not, normal otherwise
  const lvCableColor = useCallback((f, normalAlpha) => {
    const key = `lv:${f.properties?.lv_grid}`;
    if (!hasFocus) return [...(lvGridColorMap[f.properties?.lv_grid] || LV_PALETTE[0]), normalAlpha];
    if (focusedKeys.has(key)) return [255, 255, 255, normalAlpha];
    return [...(lvGridColorMap[f.properties?.lv_grid] || LV_PALETTE[0]), 30];
  }, [hasFocus, focusedKeys, lvGridColorMap]);

  const mvCableColor = useCallback((f, normalAlpha) => {
    const key = `mv:${f.properties?.mv_trafo}`;
    // Normally-open ring ties: red regardless of focus, so they stand out as N-1 switches
    if (f.properties?.is_tie) {
      const dim = hasFocus && !focusedKeys.has(key);
      return [220, 38, 38, dim ? 60 : normalAlpha];
    }
    if (!hasFocus) return [...(mvGridColorMap[f.properties?.mv_trafo] || MV_PALETTE[0]), normalAlpha];
    if (focusedKeys.has(key)) return [255, 255, 255, normalAlpha];
    return [...(mvGridColorMap[f.properties?.mv_trafo] || MV_PALETTE[0]), 30];
  }, [hasFocus, focusedKeys, mvGridColorMap]);

  const handleLVTrafoClick = useCallback(({ object }) => {
    if (!object || !onGridClick) return;
    onGridClick(`lv:${object.properties?.lv_grid}`);
  }, [onGridClick]);

  const handleMVSubClick = useCallback(({ object }) => {
    if (!object || !onGridClick) return;
    onGridClick(`mv:${object.properties?.mv_trafo}`);
  }, [onGridClick]);

  // Split LV features by geometry type
  const lvLines = useMemo(
    () => lvData ? { type: 'FeatureCollection', features: lvData.features.filter(f => f.geometry?.type === 'LineString') } : null,
    [lvData]
  );
  const lvNodes = useMemo(
    () => lvData ? { type: 'FeatureCollection', features: lvData.features.filter(f => f.geometry?.type === 'Point' && f.properties?.node_type !== 1) } : null,
    [lvData]
  );
  // Transformer features as a plain array (for ColumnLayer)
  const lvTrafos = useMemo(
    () => lvData ? lvData.features.filter(f => f.geometry?.type === 'Point' && f.properties?.node_type === 1) : [],
    [lvData]
  );

  const mvLines = useMemo(
    () => mvData ? { type: 'FeatureCollection', features: mvData.features.filter(f => f.geometry?.type === 'LineString') } : null,
    [mvData]
  );

  const mvSubstations = useMemo(
    () => mvData ? mvData.features.filter(f => f.geometry?.type === 'Point' && f.properties?.is_mv_sub === true) : [],
    [mvData]
  );

  const mvLvTrafos = useMemo(
    () => mvData ? mvData.features.filter(f => f.geometry?.type === 'Point' && f.properties?.is_lv_trafo === true) : [],
    [mvData]
  );

  const layers = useMemo(() => {
    const result = [];

    // LV cable lines — white when focused, dim when another grid is focused, normal otherwise
    if (showLV && lvLines) {
      result.push(new GeoJsonLayer({
        id: 'lv-lines',
        data: lvLines,
        stroked: true,
        filled: false,
        getLineColor: f => lvCableColor(f, 230),
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: 2,
        pickable: true,
        onHover: handleHover,
        updateTriggers: { getLineColor: [lvGridColorMap, focusTrigger] },
      }));
    }

    // LV load/bus nodes
    if (showLV && lvNodes) {
      result.push(new GeoJsonLayer({
        id: 'lv-nodes',
        data: lvNodes,
        stroked: false,
        filled: true,
        pointType: 'circle',
        getPointRadius: 4,
        pointRadiusUnits: 'pixels',
        getFillColor: f => lvCableColor(f, 200),
        pickable: true,
        onHover: handleHover,
        updateTriggers: { getFillColor: [lvGridColorMap, focusTrigger] },
      }));
    }

    // LV Transformers — always white outer, always fully visible so user can find and select them
    if (showLV && lvTrafos.length > 0) {
      const trafoHeight = f => 14 + (f.properties.capacity_MVA || 0.25) * 28;
      result.push(new ColumnLayer({
        id: 'lv-trafos-outer',
        data: lvTrafos,
        diskResolution: 32,
        radius: 7,
        getPosition: f => f.geometry.coordinates,
        getElevation: trafoHeight,
        getFillColor: [255, 255, 255, 255],
        getLineColor: [200, 200, 200, 160],
        lineWidthMinPixels: 1,
        extruded: true,
        pickable: true,
        onHover: handleHover,
        onClick: handleLVTrafoClick,
      }));
      result.push(new ColumnLayer({
        id: 'lv-trafos-inner',
        data: lvTrafos,
        diskResolution: 32,
        radius: 3,
        getPosition: f => f.geometry.coordinates,
        getElevation: f => trafoHeight(f) + 6,
        getFillColor: [10, 10, 10, 255],
        getLineColor: [60, 60, 60, 200],
        lineWidthMinPixels: 1,
        extruded: true,
        pickable: false,
      }));
    }

    // MV cable lines — white when focused, dim when another grid is focused, normal otherwise
    if (showMV && mvLines) {
      result.push(new GeoJsonLayer({
        id: 'mv-lines',
        data: mvLines,
        stroked: true,
        filled: false,
        getLineColor: f => mvCableColor(f, 240),
        getLineWidth: 6,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: 3,
        capRounded: true,
        jointRounded: true,
        lineMiterLimit: 2,
        pickable: true,
        onHover: handleHover,
        updateTriggers: { getLineColor: [mvGridColorMap, focusTrigger] },
      }));
    }

    // LV trafos inside the MV grid — amber columns, thinner than MV substations
    if (showMV && mvLvTrafos.length > 0) {
      result.push(new ColumnLayer({
        id: 'mv-lv-trafos',
        data: mvLvTrafos,
        diskResolution: 32,
        radius: 5,
        getPosition: f => f.geometry.coordinates,
        getElevation: f => 10 + (f.properties.capacity_MVA || 0.25) * 20,
        getFillColor: [251, 191, 36, 255],
        getLineColor: [180, 130, 10, 200],
        lineWidthMinPixels: 1,
        extruded: true,
        pickable: true,
        onHover: handleHover,
        onClick: handleMVSubClick,
      }));
    }

    // MV Substation buildings — always white outer, always fully visible
    if (showMV && mvSubstations.length > 0) {
      const subBase = f => 38 + (f.properties.capacity_MVA || 8) * 3;
      result.push(new PolygonLayer({
        id: 'mv-substations-outer',
        data: mvSubstations,
        extruded: true,
        wireframe: false,
        getPolygon: f => { const [lon, lat] = f.geometry.coordinates; return substationRect(lon, lat, 38, 50); },
        getElevation: subBase,
        getFillColor: [255, 255, 255, 255],
        getLineColor: [255, 255, 255, 200],
        lineWidthMinPixels: 1,
        pickable: true,
        onHover: handleHover,
        onClick: handleMVSubClick,
      }));
      result.push(new PolygonLayer({
        id: 'mv-substations-inner',
        data: mvSubstations,
        extruded: true,
        wireframe: false,
        getPolygon: f => { const [lon, lat] = f.geometry.coordinates; return substationRect(lon, lat, 18, 26); },
        getElevation: f => subBase(f) + 18,
        getFillColor: [10, 10, 10, 255],
        getLineColor: [60, 60, 60, 200],
        lineWidthMinPixels: 1,
        pickable: false,
      }));
    }

    // Buildings — 3D extruded polygons, colored by category and PV presence
    if (vis.buildings !== false && buildingsData) {
      result.push(new GeoJsonLayer({
        id: 'buildings',
        data: buildingsData,
        extruded: true,
        getElevation: f => Math.max(3, (f.properties.volume || 0) / Math.max(f.properties.area || 1, 1)) * 2,
        getFillColor: f => {
          if (f.properties.category === 'residential')
            return f.properties.pv_profile ? [2, 114, 225, 180] : [4, 142, 0, 180];
          return f.properties.pv_profile ? [228, 113, 5, 180] : [227, 3, 34, 180];
        },
        getLineColor: [255, 255, 255, 40],
        lineWidthMinPixels: 0.5,
        pickable: true,
        onHover: handleHover,
      }));
    }

    return result;
  }, [
    lvLines, lvNodes, lvTrafos, mvLines, mvSubstations, mvLvTrafos, buildingsData,
    vis, handleHover, lvGridColorMap, mvGridColorMap, showLV, showMV,
    focusTrigger, lvCableColor, mvCableColor, handleLVTrafoClick, handleMVSubClick,
  ]);

  const tooltipContent = useMemo(() => {
    if (!tooltip) return null;
    const props = tooltip.object?.properties || {};
    const keys = [
      'lv_grid', 'mv_trafo', 'cable_type', 'length',
      'category', 'net_load', 'area', 'volume', 'people', 'capacity_MVA',
    ];
    const rows = keys.filter(k => props[k] != null).map(k => (
      <div key={k}><b>{k}:</b> {String(props[k])}</div>
    ));
    return rows.length ? rows : null;
  }, [tooltip]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <DeckGL
        viewState={viewState}
        onViewStateChange={handleViewStateChange}
        controller
        layers={layers}
        style={{ position: 'absolute', inset: 0 }}
        onClick={({ object }) => { if (!object && onGridClick) onGridClick(null); }}
      >
        <Map mapStyle={MAP_STYLE} />
      </DeckGL>

      {tooltip && tooltipContent && (
        <div style={{
          position: 'absolute', left: tooltip.x + 12, top: tooltip.y - 10,
          background: 'rgba(15,23,42,0.92)', color: '#cbd5e1',
          padding: '8px 12px', borderRadius: 8, fontSize: 12,
          pointerEvents: 'none', zIndex: 100,
          border: '1px solid #334155', maxWidth: 220,
        }}>
          {tooltipContent}
        </div>
      )}
    </div>
  );
}